import { yupResolver } from '@hookform/resolvers/yup';
import React, { useContext, useEffect, useState } from 'react';
import { Controller, FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import { BasicButton } from '../../../components/common/Button';
import { InputWord } from '../../../components/common/Input';
import { ToastQueueRefContext } from '../../../components/common/Toast';
import FormHeader from '../../../components/forms/FormHeader';
import * as Icon from '../../../components/icons';
import { connexionStepTwoSchema, connexionStepTwoType } from '../../../schema/LoginSchema';
import AuthService from '../../../services/auth.service';

type TFAFormProps = {
  setPageStep: React.Dispatch<React.SetStateAction<string>>;
  connexionMethods: UseFormReturn<{ nickNameOrEmail: string; password: string }, undefined>;
  isDoubleAuthEmail: boolean;
  setIsDoubleAuthEmail: (isDoubleAuthEmail: boolean) => void;
  redirect: (check2FA?: boolean, redirectUrl?: string) => void;
  setRedirectUrl: (redirectUrl: string | undefined) => void;
  redirectUrl?: string | undefined;
};

const TFAForm = ({
  setPageStep,
  connexionMethods,
  isDoubleAuthEmail,
  setIsDoubleAuthEmail,
  redirect,
  redirectUrl,
  setRedirectUrl,
}: TFAFormProps) => {
  const [cooldown, setCooldown] = useState(0);
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
  const toastQueueRef = useContext(ToastQueueRefContext);
  const authService = new AuthService();

  const connexionStepTwoMethods = useForm<connexionStepTwoType>({
    resolver: yupResolver(connexionStepTwoSchema),
    mode: 'onSubmit',
    defaultValues: {
      code: '',
    },
  });

  const checkOneTimePassword = async ({ code }: connexionStepTwoType) => {
    await authService.checkOneTimePassword({
      nickNameOrEmail: connexionMethods.watch('nickNameOrEmail'),
      password: connexionMethods.watch('password'),
      oneTimePassword: Number(code),
    });

    redirect(true, redirectUrl);
  };

  const runCooldown = () => {
    setCooldown(60);

    const interval = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const login = async (resend: boolean) => {
    if (cooldown > 0) return;
    const loginReturn = await authService.login(
      {
        nickNameOrEmail: connexionMethods.watch('nickNameOrEmail'),
        password: connexionMethods.watch('password'),
      },
      true,
    );

    if (resend) {
      toastQueueRef?.current?.addToast({
        timer: 2000,
        description: 'Votre code de vérification a été renvoyé',
        type: 'SUCCESS',
      });
    }

    if (loginReturn.oneTimePasswordActivated) {
      runCooldown();
      setPageStep('connexionStepTwo');
      setIsDoubleAuthEmail(false);
      setRedirectUrl(loginReturn.redirect);
    } else if (loginReturn.doubleAuthEmailActivated) {
      runCooldown();
      setPageStep('connexionStepTwo');
      setIsDoubleAuthEmail(true);
      setRedirectUrl(loginReturn.redirect);
    } else {
      redirect(true, loginReturn.redirect);
    }
  };

  useEffect(() => {
    runCooldown();
  }, []);

  return (
    <>
      <FormHeader
        title={isDoubleAuthEmail ? 'Saisir le code de vérification ' : 'Entrer votre code de vérification de connexion envoyé par SMS'}
        subTitle={
          isDoubleAuthEmail
            ? 'Pour terminer votre connexion, veuillez saisir le code à 6 chiffres que nous vous avons envoyé par mail.'
            : 'Nous vous avons envoyé un code à 6 chiffres sur votre téléphone. Veuillez entrez le code ci-dessous pour vous connecter.'
        }
      />
      <FormProvider {...connexionStepTwoMethods}>
        <form onSubmit={connexionStepTwoMethods.handleSubmit(checkOneTimePassword)}>
          <Controller
            name='code'
            control={connexionStepTwoMethods.control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <>
                <InputWord
                  label='Code de vérification'
                  type='text'
                  placeholder='Ex : 12345678'
                  actionLabel={
                    <span
                      className={`text-green-secondary relative flex cursor-pointer items-center space-x-2 font-medium hover:font-semibold hover:underline ${cooldown > 0 && '!text-gray-secondary hover:!font-medium hover:!no-underline'}`}
                      onClick={() => login(true)}
                    >
                      <span>
                        Renvoyer le code
                        {tooltipIsOpen && (
                          <span className='absolute right-0 bottom-8 w-[320px] rounded-md bg-[#4F5254] p-1 text-center text-xs leading-5 text-white opacity-90'>
                            Vous pourrez renvoyer un code dans : {cooldown} seconde{cooldown > 1 && 's'}
                          </span>
                        )}
                      </span>
                      {cooldown > 0 && (
                        <Icon.Info
                          onMouseEnter={() => setTooltipIsOpen(true)}
                          onMouseLeave={() => setTooltipIsOpen(false)}
                          className='opacity-50 max-md:hidden'
                          color='#B6C4DA'
                        />
                      )}
                    </span>
                  }
                  value={value}
                  onChangeValue={onChange}
                  errorMessage={error?.message}
                />
              </>
            )}
          />
          {isDoubleAuthEmail && (
            <div className='mt-5 flex items-center space-x-5'>
              <Icon.Danger color='#E48900' className='w-[30px]' />
              <span className='text-orange-primary text-sm'>
                L’envoi du code par SMS assure une sécurité plus élevée. Nous vous recommandons de choisir ce mode de connexion.
              </span>
            </div>
          )}
          <BasicButton label='Confirmer' fullWidth={true} isSubmitButton={true} />
          <div
            onClick={() => setPageStep('connexion')}
            className='text-green-secondary mt-3 cursor-pointer text-center font-bold hover:underline'
          >
            Retour à la page de connexion
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default TFAForm;
